import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { BehaviorSubject, map, Observable, of, ReplaySubject, tap } from 'rxjs';
import { Datasource } from './datasources';
import { ExternalDatatype } from './data-source-table-route.util';

import { decode } from '@msgpack/msgpack';
import {
    TableHistoryPage,
    TableHistoryRevertResult,
} from './components/table-history/table-history.model';
@Injectable({ providedIn: 'root' })
export class TableService {
    private apiLink = environment.apiLink;
    private _httpClient = inject(HttpClient);
    constructor() {}

    /**
     * `get-table-data` / `get-external-table-data` may return grid payload under
     * `table_preview` while other fields stay at the root - normalize so callers
     * keep using `data`, `count`, `schema`, `comments`.
     */
    private normalizeDecodedTablePayload(decoded: any): any {
        if (!decoded || typeof decoded !== 'object') {
            return decoded;
        }
        const tp = decoded.table_preview;
        if (tp === undefined || tp === null) {
            return decoded;
        }
        if (Array.isArray(tp)) {
            return {
                ...decoded,
                data: tp,
                count: decoded.count ?? tp.length,
            };
        }
        if (typeof tp === 'object') {
            return {
                ...decoded,
                data: tp.data ?? [],
                count: tp.count ?? decoded.count ?? 0,
                schema: tp.schema ?? decoded.schema ?? {},
                comments: tp.comments ?? decoded.comments ?? {},
            };
        }
        return decoded;
    }

    getAll(): Observable<any[]> {
        return this._httpClient
            .get<any[]>(this.apiLink + 'data_source/getTables')
            .pipe(
                map((res: any) => {
                    return res.data.sources;
                })
            );
    }

    getAllTables(): Observable<any[]> {
        return this._httpClient
            .get<any[]>(this.apiLink + 'data_source/get-all-tables')
            .pipe(
                map((res: any) => {
                    return res.data.tables;
                })
            );
    }

    getTableByName(name: string, page: number, size: number) {
        // Calculate offset from page number: page 0 = offset 0, page 1 = offset size, etc.
        const offset = page;

        const params = new HttpParams()
            .append('name', name)
            .append('offset', offset)
            .append('limit', size);
        return this._httpClient
            .get(this.apiLink + 'data_source/get-table-data', {
                params,
                responseType: 'arraybuffer',
                headers: {
                    Accept: 'application/x-msgpack',
                },
            })
            .pipe(
                map((buffer: ArrayBuffer) => {
                    const decodedData = decode(new Uint8Array(buffer));
                    return this.normalizeDecodedTablePayload(decodedData);
                })
            );
    }

    /** Table metadata (structure text, about, KPI definitions) for internal tables. */
    getTableMeta(name: string): Observable<any> {
        const params = new HttpParams().append('name', name);
        return this._httpClient
            .get<any>(this.apiLink + 'data_source/get-table-meta', { params })
            .pipe(map((res: any) => res?.data ?? res));
    }

    /** Same as {@link getTableMeta} for external engine tables. */
    getExternalTableMeta(name: string, dataSourceId: string): Observable<any> {
        const params = new HttpParams()
            .append('name', name)
            .append('dataSourceId', String(dataSourceId));
        return this._httpClient
            .get<any>(this.apiLink + 'data_source/get-external-table-meta', {
                params,
            })
            .pipe(map((res: any) => res?.data ?? res));
    }

    getExternalTableData(name: string, page: number, size: number, datasourceId?) {
        const offset = page * size;

        let params = new HttpParams()
            .append('name', name)
            .append('offset', offset)
            .append('limit', size);
        if (datasourceId) {
            params = params.append('dataSourceId', datasourceId);
        }
        return this._httpClient
            .get(this.apiLink + 'data_source/get-external-table-data', {
                params,
                responseType: 'arraybuffer',
                headers: {
                    Accept: 'application/x-msgpack',
                },
            })
            .pipe(
                map((buffer: ArrayBuffer) => {
                    const decodedData = decode(new Uint8Array(buffer));
                    return this.normalizeDecodedTablePayload(decodedData);
                })
            );
    }

    create(modal: any) {
        return this._httpClient.post(
            this.apiLink + 'data_source/new-data',
            modal
        );
    }

    upload(modal: any) {
        return this._httpClient.post(
            this.apiLink + 'data_source/upload',
            modal,
            { responseType: 'text' }
        );
    }
    changeTableDecription(modal: any) {
        return this._httpClient.post(
            this.apiLink + 'data_source/change-table-desc',
            modal
        );
    }

    changeExternalTableDescription(body: {
        tableName: string;
        description: string;
        structure: string;
        dataSourceId: string;
    }) {
        return this._httpClient.post(
            this.apiLink + 'data_source/change-external-table-desc',
            body
        );
    }

    /** POST /v1/data_source/set-column-lock */
    setColumnLock(body: {
        tableName: string;
        dataSourceId: string | number;
        columnName: string;
        locked: boolean;
        sourceType?: ExternalDatatype;
    }): Observable<unknown> {
        return this._httpClient.post(
            this.apiLink + 'data_source/set-column-lock',
            body
        );
    }

    /** POST /v1/data_source/set-table-agent-write (internal tables only) */
    setTableAgentWrite(body: {
        tableName: string;
        dataSourceId: string | number;
        allowAgentWrite: boolean;
    }): Observable<{ done: boolean; allowAgentWrite: boolean }> {
        return this._httpClient.post<{
            done: boolean;
            allowAgentWrite: boolean;
        }>(this.apiLink + 'data_source/set-table-agent-write', body);
    }

    /** GET /v1/data_source/table-history (internal tables only) */
    getTableHistory(
        name: string,
        limit = 50,
        offset = 0
    ): Observable<TableHistoryPage> {
        const params = new HttpParams()
            .set('name', name)
            .set('limit', limit)
            .set('offset', offset);
        return this._httpClient
            .get<{ data: TableHistoryPage }>(
                this.apiLink + 'data_source/table-history',
                { params }
            )
            .pipe(map(res => res.data));
    }

    /** POST /v1/data_source/table-history/:historyId/revert */
    revertTableHistory(
        name: string,
        historyId: string
    ): Observable<TableHistoryRevertResult> {
        return this._httpClient
            .post<{ data: TableHistoryRevertResult }>(
                `${this.apiLink}data_source/table-history/${encodeURIComponent(historyId)}/revert`,
                { name }
            )
            .pipe(map(res => res.data));
    }

    generateTableDecription(modal: any) {
        return this._httpClient.post(
            this.apiLink + 'data_source/generate-table-desc',
            modal
        );
    }

    delete(name: string) {
        const params = new HttpParams().append('name', name);
        return this._httpClient.delete(
            this.apiLink + 'data_source/delete-table',
            { params }
        );
    }

    getAllTablesNames() {
        return this._httpClient
            .get(this.apiLink + 'data_source/get-all-names')
            .pipe(
                map((res: any) => {
                    return res.data.tables;
                })
            );
    }

}
